import { apiSlice } from '../../../api/common/apiSlice';
import { ApiTagType } from '../../../constants/apiTagType';
import {
  CreateEmailTemplateRequest,
  CreatePushTemplateRequest,
  EmailNotificationTemplate,
  EmailTemplateHistoryEntry,
  PushNotificationTemplate,
  PushTemplateHistoryEntry,
  UpdateEmailTemplateRequest,
  UpdatePushTemplateRequest,
} from '../../../types/notificationTemplate';

/** Push and email templates (MAD-4965). The ids of the two tables overlap, so every tag id carries the channel. */
const PUSH_URL = '/admin/notification-template/push';
const EMAIL_URL = '/admin/notification-template/email';

export const notificationTemplateApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    fetchPushTemplates: builder.query<PushNotificationTemplate[], void>({
      query: () => ({ url: PUSH_URL, method: 'GET' }),
      providesTags: (res) => [
        ...(res || []).map(({ id }) => ({ type: ApiTagType.NotificationTemplate as const, id: `push-${id}` })),
        { type: ApiTagType.NotificationTemplate, id: 'PUSH-LIST' },
      ],
    }),
    fetchPushTemplateHistory: builder.query<PushTemplateHistoryEntry[], number>({
      query: (id) => ({ url: `${PUSH_URL}/${id}/history`, method: 'GET' }),
      providesTags: (res, error, id) => [{ type: ApiTagType.NotificationTemplateHistory, id: `push-${id}` }],
    }),
    createPushTemplate: builder.mutation<PushNotificationTemplate, CreatePushTemplateRequest>({
      query: (data) => ({ url: PUSH_URL, method: 'POST', body: data }),
      invalidatesTags: [{ type: ApiTagType.NotificationTemplate, id: 'PUSH-LIST' }],
    }),
    updatePushTemplate: builder.mutation<PushNotificationTemplate, UpdatePushTemplateRequest>({
      query: ({ id, ...data }) => ({ url: `${PUSH_URL}/${id}`, method: 'PATCH', body: data }),
      invalidatesTags: (res, error, req) => [
        { type: ApiTagType.NotificationTemplate, id: `push-${req.id}` },
        { type: ApiTagType.NotificationTemplateHistory, id: `push-${req.id}` },
      ],
    }),
    fetchEmailTemplates: builder.query<EmailNotificationTemplate[], void>({
      query: () => ({ url: EMAIL_URL, method: 'GET' }),
      providesTags: (res) => [
        ...(res || []).map(({ id }) => ({ type: ApiTagType.NotificationTemplate as const, id: `email-${id}` })),
        { type: ApiTagType.NotificationTemplate, id: 'EMAIL-LIST' },
      ],
    }),
    fetchEmailTemplateHistory: builder.query<EmailTemplateHistoryEntry[], number>({
      query: (id) => ({ url: `${EMAIL_URL}/${id}/history`, method: 'GET' }),
      providesTags: (res, error, id) => [{ type: ApiTagType.NotificationTemplateHistory, id: `email-${id}` }],
    }),
    createEmailTemplate: builder.mutation<EmailNotificationTemplate, CreateEmailTemplateRequest>({
      query: (data) => ({ url: EMAIL_URL, method: 'POST', body: data }),
      invalidatesTags: [{ type: ApiTagType.NotificationTemplate, id: 'EMAIL-LIST' }],
    }),
    updateEmailTemplate: builder.mutation<EmailNotificationTemplate, UpdateEmailTemplateRequest>({
      query: ({ id, ...data }) => ({ url: `${EMAIL_URL}/${id}`, method: 'PATCH', body: data }),
      invalidatesTags: (res, error, req) => [
        { type: ApiTagType.NotificationTemplate, id: `email-${req.id}` },
        { type: ApiTagType.NotificationTemplateHistory, id: `email-${req.id}` },
      ],
    }),
  }),
});

export const {
  useFetchPushTemplatesQuery,
  useFetchPushTemplateHistoryQuery,
  useCreatePushTemplateMutation,
  useUpdatePushTemplateMutation,
  useFetchEmailTemplatesQuery,
  useFetchEmailTemplateHistoryQuery,
  useCreateEmailTemplateMutation,
  useUpdateEmailTemplateMutation,
} = notificationTemplateApiSlice;
